<script setup lang="ts">
  import {useI18n} from 'vue-i18n';
  import {computed} from 'vue';
  import useCpBem from '~/composables/useCpBem';
  import CpCardAccordion from '~/components/cp/CpCardAccordion/CpCardAccordion.vue';
  import CpCardFaq from '~/components/cp/CpCardFaq/CpCardFaq.vue';
  import CpSectionPdpPriceShipping from '~/components/cp/CpSection/CpSectionPdpPriceShipping/CpSectionPdpPriceShipping.vue';
  import CpCardNavigateToProductQty from '~/components/cp/CpCardNavigateToProductQty/CpCardNavigateToProductQty.vue';
  import CpButton from '~/components/cp/CpButton/CpButton.vue';
  import CpText from '~/components/cp/CpText/CpText.vue';
  import LazyCpMarkdown from '~/components/cp/CpMarkdown/CpMarkdown.vue';
  import CpSectionHeader from '~/components/cp/CpSection/CpSectionHeader/CpSectionHeader.vue';
  import facebookFaq from '~/assets/img/facebook_faq.png';
  import {EXTERNAL_LINKS} from '~/utils';

  const {b, e} = useCpBem('cp-section-misc-faq');
  const {t} = useI18n();

  const props = withDefaults(defineProps<{
    kind: 'Purchase' | 'Shipping'
  }>(), { });

  const firstText = `Te invitamos a ver las opiniones de nuestros clientes en <a href="${EXTERNAL_LINKS.ekomi}" target="_blank">ekomi</a>.`;
  const secondText = 'Visita la sección [Quiénes somos](/Quienes-somos/) y conoce más sobre la empresa';
  const thirdText = 'Síguenos en nuestras redes sociales y únete a la comunidad.';
  const freeShipping = 'A través de este [enlace](/catalog/cat/4fadfd9ad0936/%7B"instock":1,"envio":1%7D) puedes encontrar todos los artículos que actualmente tienen envío gratuito.';

  interface IContent {
    expanded: boolean,
    title: string;
    body: string;
    bottomComponent?: any[];
  }

  const purchaseQuestions: IContent[] = [
    {
      expanded: false,
      title: '¿Es seguro comprar en Cyberpuerta?',
      body: '¡Así es!, contamos con certificación oficial de la Asociación Mexicana de Internet (AMIPCI) quien regula nuestra actividad como una empresa legalmente constituida, además, toda la información en el sitio se encuentra segura gracias a la encriptación ofrecida por el servicio de Norton Secured. \n\nSólo con clientes satisfechos podemos seguir creciendo, apreciamos mucho la confianza que los clientes ponen en nosotros al comprar en www.cyberpuerta.mx',
      bottomComponent: [
        {component: CpCardFaq, props: {title: 'Sello de Plata eKomi', image: 'https://www.ekomi.es/images/es/produkt/siegel/zerti_silber_new.png', style: 'margin-top: 16px'}, slot: [{component: LazyCpMarkdown, props: {source: firstText, class: e`body-content`}}]},
        {component: CpCardFaq, props: {title: 'Cyberpuerta', image: 'https://www.cyberpuerta.mx/out/pictures/wysiwigpro/new/cyberpuerta.png', style: 'margin-top: 8px'}, slot: [{component: LazyCpMarkdown, props: {source: secondText, class: e`body-content`}}]},
        {component: CpCardFaq, props: {title: '¡Síguenos!', image: facebookFaq, style: 'margin-top: 8px'}, slot: [{component: LazyCpMarkdown, props: {source: thirdText, class: e`body-content`}}]},
      ],
    },
    {
      expanded: false,
  title:
      '¿Tienen tiendas donde pueda ver o comprar la mercancía en persona?',
      body: `
Con la finalidad de ofrecer un amplio catálogo de producto al mejor precio,
no contamos con piso de exhibición o tienda física para la recolección del producto,
todos los artículos se envían desde la red de almacenes a lo largo del país
con base a disponibilidad, para su entrega terrestre en un lapso desde 1 a 4
hábiles hasta tu destino.

Si tienes alguna duda, el área de servicio al cliente te puede apoyar vía teléfono,
chat o correo para aclarar cualquier duda acerca de las características de
nuestros artículos.
`,
    },
    {
      expanded: false,
      title: '¿Puedo pagar a Meses Sin Intereses o con pagos diferidos?',
      body: `
Ofrecemos periódicamente campañas de meses sin intereses.
Lamentablemente, en estos momentos no contamos con una campaña de meses sin intereses.

Opciones de pagos diferidos hasta 24* meses disponibles en todas tus compras.
Aplica un costo adicional que será desglosado transparentemente en todo momento.

Al ingresar la tarjeta de crédito durante el proceso de compra,
se mostrará la cantidad de meses disponible para la tarjeta seleccionada.

*Algunas mensualidades están sujetas a la disponibilidad y condiciones de tu tarjeta de crédito.
        `,
    },
    {
      expanded: false,
      title: '¿Los precios incluyen IVA, me pueden dar factura?',
      body: `Todos los productos publicados en el sitio incluyen IVA y es posible facturarlos,
      por disposición oficial solo requerimos el RFC y la razón social para generarla; esta
      información será requerida al procesar tu carrito de compra o puedes modificarla en
      cualquier momento desde el apartado "Datos de envío, pago y facturación" desde "Mi cuenta"
      en el portal. En caso de no contar con esa información se generará una
      factura genérica como comprobante de compra.`,
    },
    {
      expanded: false,
      title: '¿Cómo solicito una refacturación?',
      body: `Dispones de 24 horas naturales para realizar cualquier modificación a
          partir de la emisión de la misma. Puedes modificar tu factura desde el
          apartado de "Facturación" y posteriormente “Modificar datos de
          facturación” desde tu "[Historial de Pedidos](/account/orders)" en un lapso no mayor
          a 24 horas naturales desde su emisión.`,
    },
    {
      expanded: false,
      title: '¿Tienen garantía?',
          body: `Todos los productos que comercializamos tienen por lo menos 1
          año de garantía directamente con la marca por cualquier defecto de
          fabricación, podemos apoyarte con la gestión. La vigencia de la
          garantía aplica desde la fecha de compra que aparece en el comprobante
          que recibes al efectuar tu pago.`,
    },
    {
      expanded: false,
      title: '¿No vivo en México pero me interesa comprar en Cyberpuerta?',
          body: `Agradecemos tu interés, lamentablemente, en cumplimiento
          con las normas oficiales, no realizamos envíos de producto fuera
          de la República Mexicana.`,
    },
    {
      expanded: false,
      title: '¿Dónde puedo ver el precio y existencia?',
      body: `
Toda existencia y precio mostrado en el portal es el precio actual,
este se actualiza constantemente, el precio ya incluye IVA y puedes
visualizarlo en el artículo de tu interés.
      `,
      bottomComponent: [
        {
          component: CpText,
          text: 'Piezas disponibles',
          props: {variant: 'body-bold', block: true, class: 'cp-section-misc-faq__pieces'},
        },
        {
          component: CpCardNavigateToProductQty,
          props: {esd: false, stock: 100, piecesToAdd: 10, lowStock: false, class: 'cp-section-misc-faq__product-qty'},
        },
        {
          component: CpText,
          text: `Si el producto no se encuentra en existencia de momento, puedes registrar tu correo
              en el botón de “Crear alerta de disponibilidad” para recibir una alerta cuando esté disponible de nuevo.`,
          props: {variant: 'body-regular', block: true, class: 'cp-section-misc-faq__alert-text'},
        },
        {
          component: CpButton,
          props: {label: 'Crear alerta de disponibilidad', kind: 'iconlabel', icon: 'add_alert', fullWidth: true, style: 'margin: 48px 0', noPadding: true},
        },
      ],
    },
  ];

  const shippingQuestions: IContent[] = [
    {
      expanded: false,
      title: '¿Puedo recoger mi pedido en algún establecimiento?',
      body: 'No contamos con piso de exhibición o punto para recolección de producto, todos los pedidos son enviados a través de la red de almacenes de la marca a lo largo del país con los servicios de logística. Por seguridad en el envío, no realizamos entregas personalmente.',
    },
    {
      expanded: false,
      title: '¿Puedo tener envío gratis?',
      body: 'Todos los pedidos cuentan con seguro de envío contra cualquier percance como robo extravío o daño, dado que tu pedido puede surtirse desde distintos puntos de origen, no podemos ofrecer envío gratis. La buena noticia es que constantemente tenemos promociones con envíos gratuitos en diferentes artículos, puedes identificarlos con la leyenda correspondiente de envío debajo de su precio:\n',
      bottomComponent: [
        {component: CpSectionPdpPriceShipping, props: {product: {freeShipping: true, price: 839, shipping: 839}, class: 'cp-section-misc-faq__shipping'}},
        {component: LazyCpMarkdown, props: {source: freeShipping, class: e`body-content`}},
      ],
    },
    {
      expanded: false,
      title: '¿Si pido más de una pieza tengo que pagar doble envío?',
          body: 'El costo de envío se calcula con base en el peso y volumen total de la mercancía que agregaste al carrito de compras. Si agregas dos o más piezas del mismo producto no se cobrará para cada una, únicamente se añadirá el costo proporcional por estas. Para comprobar el total de tu pedido, simplemente agrega los artículos y después de click en el botón “Carrito de Compras”.',
    },
    {
      expanded: false,
      title: 'Me urge mi pedido, ¿me lo pueden enviar express?',
          body: 'Todos nuestros envíos tardan de 1 a 4 días hábiles. Estamos trabajando para ofrecer esta opción próximamente.',
    },
    {
      expanded: false,
      title: '¿Cuál es el tiempo de entrega?',
          body: 'Enviamos a todo México con cobertura terrestre en un plazo de 1 a 4 días hábiles. Sin embargo, puede variar dependiendo de tu código postal. Al registrarte y crear tu cuenta, podrás ver el tiempo de entrega en la página de productos y también durante el proceso de compra en el carrito.',
    },
    {
      expanded: false,
      title: '¿Cómo se calcula el costo de envío?',
      body: 'El costo de envío se calcula con base a las dimensiones, peso y volumen de los paquetes necesarios para surtir tu orden. Si tu pedido será surtido desde más de un almacén, Cyberpuerta cubrirá la diferencia, no se hacen cargos adicionales para que todos tus artículos viajen seguros hasta tu domicilio.',
    },
  ];

  const content = computed(() => {
    return props.kind === 'Purchase' ? purchaseQuestions : shippingQuestions;
  });

  const title = computed(() => {
    return props.kind === 'Purchase' ? t('purchaseTitle') : t('shippingTitle');
  });

</script>

<template>
    <div :class="b">
        <ClientOnly>
            <CpSectionHeader :title="title" :show-top-border="true" type="h2" />
            <div :class="e('container')">
                <CpCardAccordion
                    v-for="item in content"
                    :key="item.title"
                    :class="e`card`"
                    :title="item.title"
                    title-variant="body-regular"
                    title-type="primary"
                    :expanded="item.expanded"
                >
                    <component :is="LazyCpMarkdown" :source="item.body" :class="e`body-content`" />
                    <component
                        :is="component.component"
                        v-for="component in item.bottomComponent"
                        :key="component"
                        v-bind="component.props"
                    >
                        {{ component.text }}
                        <component
                            :is="child.component"
                            v-for="child in component.slot"
                            :key="child"
                            v-bind="child.props"
                        >
                            {{ child.slot }}
                        </component>
                    </component>
                </CpCardAccordion>
            </div>
        </ClientOnly>
    </div>
</template>

<style scoped lang="scss">
  @import "assets/style/global.scss";

  .cp-section-misc-faq {

    &__container {
      padding: $cp-gutter;
    }

    &__card {
      padding-bottom: $cp-gutter;
    }

    &__pieces {
      margin: $cp-gutter-xlarge 0 $cp-gutter;
      width: 100%;
      border-top: $cp-border-standard;
      padding-top: $cp-gutter;
      padding-left: $cp-gutter;
    }

    &__product-qty {
      width: calc(100% - 32px);
      margin: 0 16px;
    }

    &__alert-text {
      border-top: $cp-border-standard;
      padding-top: $cp-gutter;
      margin: 16px 0;
    }

    &__shipping {
      border-top: $cp-border-standard;
      margin: $cp-gutter-xxlarge 0;
      border-bottom: 0;
    }
  }
</style>

<i18n locale="es-US">
  purchaseTitle: 'Dudas sobre la compra'
  shippingTitle: 'Dudas sobre el envío'
</i18n>
